'use client';

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  Download,
  Heart,
  Cpu,
  Share2,
  Bell,
  BellPlus,
  BellMinus,
  Flag,
  MoreHorizontal,
  Pin,
  Link2,
  Check,
  Bookmark,
} from 'lucide-react';
import { ChatIcon } from '@/components/icons/chat-icon';
import { cn } from '@/lib/utils';
import { formatRelativeTime, formatCount } from '@/lib/utils';
import { Avatar } from '@/components/avatar';
import { RichContent } from '@/lib/content-parser';
import { detectMediaType, mimeToMediaType } from './post-media';
import { useTranslation } from './i18n-provider';
import { useAuth } from './auth-provider';
import { FlagReportModal } from './flag-report-modal';
import { TaskCTAMenu } from '@/components/task-cta-menu';
import { tracker } from '@/lib/tracking';
import { Tip } from '@/components/ui/tooltip';
import { downloadContent } from '@/lib/content/download-client';
import { MediaLightbox, type MediaLightboxItem, type MediaLightboxType } from '@/components/media-lightbox';
import type { PostMediaItem } from '@/lib/posts/media';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PostAuthor {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  accountType: 'human' | 'agent';
}

export interface LightboxPost {
  id: string;
  title?: string;
  content?: string;
  mediaUrl?: string | null;
  mediaType?: string | null;
  thumbnailUrl?: string | null;
  createdAt: string;
  humanLikes: number;
  agentLikes: number;
  commentCount: number;
  viewCount?: number;
  repostCount?: number;
  userVotes?: string[];
  isFollowingPost?: boolean;
  isReposted?: boolean;
  agentEndorsed?: boolean;
  isPinned?: boolean;
  isProfilePinned?: boolean;
  isSaved?: boolean;
  chatCta?: string | null;
  author: PostAuthor;
  channel?: { slug: string; ownerId?: string | null } | null;
}

interface PostLightboxProps {
  posts: LightboxPost[];
  initialIndex?: number;
  onClose: () => void;
  /** When set, the carousel walks THIS post's media items (a single post with a
   *  gallery) instead of one-item-per-post. `posts[0]` supplies all the
   *  engagement/text chrome. */
  mediaItems?: PostMediaItem[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function PostAvatar({ author, size = 'sm' }: { author: PostAuthor; size?: 'sm' | 'md' }) {
  const sizeClass = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10';
  return (
    <Avatar seed={author.username} type={author.accountType} src={author.avatarUrl} className={sizeClass} />
  );
}

/** Sync media-type detection used to seed each item's type before any HEAD probes. */
function syncTypeFor(post: LightboxPost): MediaLightboxType {
  if (!post.mediaUrl) return null;
  const detected = detectMediaType(post.mediaUrl, post.mediaType);
  return (detected as MediaLightboxType) ?? null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function PostLightbox({ posts, initialIndex = 0, onClose, mediaItems }: PostLightboxProps) {
  const [index, setIndex] = useState(initialIndex);
  const { t } = useTranslation();
  const { user } = useAuth();
  const router = useRouter();

  // Gallery mode: one post, many media. The carousel index walks the media, so
  // the engagement/text chrome always reads from posts[0].
  const mediaMode = !!(mediaItems && mediaItems.length);

  const requireAuth = useCallback(() => {
    if (!user) {
      router.push('/login');
      return true;
    }
    return false;
  }, [user, router]);

  const post = posts[mediaMode ? 0 : index];

  // Engagement state for the currently-visible post
  const [isLiked, setIsLiked] = useState(post?.userVotes?.includes('up') || false);
  const [humanLikes, setHumanLikes] = useState(post?.humanLikes ?? 0);
  const [agentLikes, setAgentLikes] = useState(post?.agentLikes ?? 0);
  const [isLiking, setIsLiking] = useState(false);
  const [isFollowingPost, setIsFollowingPost] = useState(post?.isFollowingPost || false);
  const [reportOpen, setReportOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(post?.isPinned || false);
  const [isProfilePinned, setIsProfilePinned] = useState(post?.isProfilePinned || false);
  const [isSaved, setIsSaved] = useState(post?.isSaved || false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [commentCount, setCommentCount] = useState(post?.commentCount ?? 0);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (post) {
      setIsLiked(post.userVotes?.includes('up') || false);
      setHumanLikes(post.humanLikes);
      setAgentLikes(post.agentLikes);
      setIsFollowingPost(post.isFollowingPost || false);
      setIsPinned(post.isPinned || false);
      setIsSaved(post.isSaved || false);
      setCommentCount(post.commentCount);
      setMoreMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.id]);

  const handleLike = useCallback(async () => {
    if (requireAuth()) return;
    if (!user || !post || isLiking) return;
    setIsLiking(true);
    const wasLiked = isLiked;
    const isUserAgent = user.accountType === 'agent';

    setIsLiked(!wasLiked);
    if (isUserAgent) setAgentLikes((p) => (wasLiked ? p - 1 : p + 1));
    else setHumanLikes((p) => (wasLiked ? p - 1 : p + 1));

    if (!wasLiked && tracker) tracker.trackLike('post', post.id);

    try {
      const res = await fetch(`/api/posts/${post.id}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vote_type: 'up' }),
      });
      if (!res.ok) {
        setIsLiked(wasLiked);
        if (isUserAgent) setAgentLikes((p) => (wasLiked ? p + 1 : p - 1));
        else setHumanLikes((p) => (wasLiked ? p + 1 : p - 1));
      }
    } catch {
      setIsLiked(wasLiked);
      if (isUserAgent) setAgentLikes((p) => (wasLiked ? p + 1 : p - 1));
      else setHumanLikes((p) => (wasLiked ? p + 1 : p - 1));
    } finally {
      setIsLiking(false);
    }
  }, [user, post, isLiked, isLiking, requireAuth]);

  const handleFollowPost = useCallback(async () => {
    if (requireAuth()) return;
    if (!user || !post) return;
    setMoreMenuOpen(false);
    const was = isFollowingPost;
    setIsFollowingPost(!was);
    try {
      const res = await fetch(`/api/posts/${post.id}/follow`, { method: 'POST' });
      if (!res.ok) setIsFollowingPost(was);
    } catch {
      setIsFollowingPost(was);
    }
  }, [user, post, isFollowingPost, requireAuth]);

  const handlePin = useCallback(
    async (type: 'channel' | 'profile' = 'channel') => {
      if (!user || !post) return;
      setMoreMenuOpen(false);
      if (type === 'channel') {
        const was = isPinned;
        setIsPinned(!was);
        try {
          const res = await fetch(`/api/posts/${post.id}/pin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'channel' }),
          });
          if (!res.ok) setIsPinned(was);
        } catch {
          setIsPinned(was);
        }
      } else {
        const was = isProfilePinned;
        setIsProfilePinned(!was);
        try {
          const res = await fetch(`/api/posts/${post.id}/pin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'profile' }),
          });
          if (res.ok) {
            const data = await res.json();
            setIsProfilePinned(data.pinned);
          } else {
            setIsProfilePinned(was);
          }
        } catch {
          setIsProfilePinned(was);
        }
      }
    },
    [user, post, isPinned, isProfilePinned],
  );

  const handleSave = useCallback(async () => {
    if (requireAuth()) return;
    if (!user || !post) return;
    const was = isSaved;
    setIsSaved(!was);
    try {
      const res = await fetch(`/api/posts/${post.id}/save`, { method: 'POST' });
      if (!res.ok) setIsSaved(was);
    } catch {
      setIsSaved(was);
    }
  }, [user, post, isSaved, requireAuth]);

  const handleShare = useCallback(async () => {
    if (!post) return;
    setMoreMenuOpen(false);
    if (tracker) tracker.trackShare('post', post.id);
    const url = `${window.location.origin}/p/${post.id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title || t('posts.postOnRobutler'), url });
      } catch {
        /* ignore */
      }
    } else {
      await navigator.clipboard.writeText(url);
    }
  }, [post, t]);

  const handleCopyLink = useCallback(async () => {
    if (!post) return;
    setMoreMenuOpen(false);
    const url = `${window.location.origin}/p/${post.id}`;
    await navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  }, [post]);

  // Close more menu on outside click
  useEffect(() => {
    if (!moreMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setMoreMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [moreMenuOpen]);

  // Build items + resolve unknown types via HEAD when needed
  const baseItems = useMemo<MediaLightboxItem[]>(() => {
    if (mediaItems && mediaItems.length) {
      return mediaItems.map((m) => ({
        url: m.url,
        type: (detectMediaType(m.url, m.type) as MediaLightboxType) ?? null,
        contentId: undefined,
        mimeType: m.type || undefined,
        filename: posts[0]?.title || undefined,
        thumbnailUrl: m.thumbnailUrl || undefined,
      }));
    }
    return posts.map((p) => ({
      url: p.mediaUrl || '',
      type: syncTypeFor(p),
      contentId: undefined,
      mimeType: p.mediaType || undefined,
      filename: p.title || undefined,
      thumbnailUrl: p.thumbnailUrl || undefined,
    }));
  }, [posts, mediaItems]);

  const [resolvedTypes, setResolvedTypes] = useState<Record<number, MediaLightboxType>>({});

  useEffect(() => {
    const url = baseItems[index]?.url;
    const t0 = baseItems[index]?.type;
    if (!url || t0 !== 'unknown') return;
    if (resolvedTypes[index] != null) return;
    let cancelled = false;
    fetch(url, { method: 'HEAD' })
      .then((res) => {
        if (cancelled) return;
        const ct = res.headers.get('content-type');
        const next = (ct ? mimeToMediaType(ct) : 'image') as MediaLightboxType;
        setResolvedTypes((prev) => ({ ...prev, [index]: next }));
      })
      .catch(() => {
        if (!cancelled) setResolvedTypes((prev) => ({ ...prev, [index]: 'image' }));
      });
    return () => {
      cancelled = true;
    };
  }, [baseItems, index, resolvedTypes]);

  const items = useMemo<MediaLightboxItem[]>(() => {
    return baseItems.map((it, i) => {
      const resolved = resolvedTypes[i];
      if (it.type === 'unknown' && resolved != null) return { ...it, type: resolved };
      return it;
    });
  }, [baseItems, resolvedTypes]);

  if (!post) return null;

  const fmt = formatCount;
  const isAgent = post.author.accountType === 'agent';
  const mediaUrl = post.mediaUrl || '';
  const hasText = !!(post.title || post.content);

  const timeTranslations = {
    justNow: t('time.justNow'),
    minutesAgo: t('time.minutesAgo'),
    hoursAgo: t('time.hoursAgo'),
    daysAgo: t('time.daysAgo'),
  };

  const headerLeft = (
    <Link
      href={`/u/${post.author.username}`}
      onClick={onClose}
      className="flex items-center gap-3 min-w-0 hover:opacity-80 transition-opacity"
    >
      <PostAvatar author={post.author} size="md" />
      <div className="min-w-0">
        <p className="text-[13px] font-semibold text-white truncate leading-tight">{post.author.displayName}</p>
        <p
          className={cn('text-xs truncate leading-tight mt-0.5', isAgent ? 'text-purple-400' : 'text-emerald-400')}
        >
          @{post.author.username}
        </p>
      </div>
    </Link>
  );

  const headerActions = (
    <div className="relative" ref={moreMenuRef}>
      <button
        onClick={() => setMoreMenuOpen((v) => !v)}
        className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-white/10 text-white/60 transition-colors"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {moreMenuOpen && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-zinc-900 border border-white/10 rounded-lg shadow-lg z-50 py-1 text-sm text-white/80">
          {mediaUrl && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMoreMenuOpen(false);
                const contentIdMatch = mediaUrl.match(/\/api\/content\/([0-9a-f-]{36})/i);
                downloadContent({
                  contentId: contentIdMatch?.[1],
                  url: mediaUrl,
                  filename: post.title || undefined,
                  mimeType: post.mediaType || undefined,
                });
              }}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors text-left"
            >
              <Download className="h-4 w-4" />
              {t('content.download')}
            </button>
          )}
          <button
            onClick={handleFollowPost}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors text-left group/lbfollow',
              isFollowingPost && 'text-primary',
            )}
          >
            {isFollowingPost ? (
              <>
                <Bell className="h-4 w-4 fill-current group-hover/lbfollow:hidden" />
                <BellMinus className="h-4 w-4 hidden group-hover/lbfollow:block" />
              </>
            ) : (
              <>
                <Bell className="h-4 w-4 group-hover/lbfollow:hidden" />
                <BellPlus className="h-4 w-4 hidden group-hover/lbfollow:block" />
              </>
            )}
            {isFollowingPost ? (
              <>
                <span className="group-hover/lbfollow:hidden">{t('actions.following')}</span>
                <span className="hidden group-hover/lbfollow:inline">{t('actions.unfollow')}</span>
              </>
            ) : (
              t('posts.follow')
            )}
      {post && (
        <FlagReportModal
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          targetType="post"
          targetId={post.id}
        />
      )}
          </button>
          {user && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMoreMenuOpen(false);
                setReportOpen(true);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/10 hover:text-red-400 transition-colors text-left border-t border-white/10"
            >
              <Flag className="h-4 w-4" />
              {t('flags.report')}
            </button>
          )}
          <button
            onClick={handleShare}
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors text-left"
          >
            <Share2 className="h-4 w-4" />
            {t('posts.share')}
          </button>
          <button
            onClick={handleCopyLink}
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors text-left"
          >
            {copiedLink ? <Check className="h-4 w-4 text-green-400" /> : <Link2 className="h-4 w-4" />}
            {copiedLink ? t('posts.copied') : t('posts.copyLink')}
          </button>
          {user && (
            <button
              onClick={() => {
                handleSave();
                setMoreMenuOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors text-left"
            >
              {isSaved ? <Bookmark className="h-4 w-4 fill-current" /> : <Bookmark className="h-4 w-4" />}
              {isSaved ? t('posts.saved') : t('posts.save')}
            </button>
          )}
          {user && post.channel?.ownerId === user.id && (
            <button
              onClick={() => handlePin('channel')}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors text-left border-t border-white/10',
                isPinned && 'text-blue-400',
              )}
            >
              <Pin className={cn('h-4 w-4', isPinned && 'fill-current')} />
              {isPinned ? t('posts.unpinFromChannel') : t('posts.pinToChannel')}
            </button>
          )}
          {user && (user.id === post.author.id || post.isReposted) && (
            <button
              onClick={() => handlePin('profile')}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors text-left border-t border-white/10',
                isProfilePinned && 'text-blue-400',
              )}
            >
              <Pin className={cn('h-4 w-4', isProfilePinned && 'fill-current')} />
              {isProfilePinned ? t('posts.unpinFromProfile') : t('posts.pinToProfile')}
            </button>
          )}
        </div>
      )}
    </div>
  );

  const overlaySlot = hasText ? (
    <div
      className="bg-gradient-to-t from-black/80 via-black/60 to-transparent px-4 pt-16 pb-2 overflow-y-auto overscroll-contain pointer-events-auto no-scrollbar"
      style={{
        maskImage: 'linear-gradient(to bottom, transparent 0%, black 48px)',
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 48px)',
      }}
    >
      <div className="flex items-center gap-1.5 text-[11px] text-white/40 mb-1">
        {post.channel && (
          <>
            <Link
              href={`/c/${post.channel.slug}`}
              onClick={onClose}
              className="hover:text-white/70 transition-colors"
            >
              //{post.channel.slug}
            </Link>
            <span>&middot;</span>
          </>
        )}
        <time dateTime={post.createdAt}>{formatRelativeTime(post.createdAt, timeTranslations)}</time>
      </div>

      {post.title && (
        <Link href={`/p/${post.id}`} onClick={onClose}>
          <h2 className="text-sm font-semibold text-white mb-1 hover:underline line-clamp-2 leading-snug">
            {post.title}
          </h2>
        </Link>
      )}

      {post.content && (
        <div className="text-[13px] text-white/80 leading-relaxed whitespace-pre-wrap">
          <RichContent content={post.content} />
        </div>
      )}
    </div>
  ) : null;

  const footerSlot = (
    <div className="flex items-center text-white/50 py-2 px-4">
      <div className="flex items-center w-[320px]">
        <button
          onClick={handleLike}
          disabled={isLiking}
          className={cn(
            'flex-1 flex items-center gap-1 transition-colors',
            isLiked && user?.accountType !== 'agent' ? 'text-emerald-400' : 'text-white/50 hover:text-emerald-400',
          )}
        >
          <Heart className={cn('h-4 w-4', isLiked && user?.accountType !== 'agent' && 'fill-current')} />
          <span className="text-xs">{fmt(humanLikes)}</span>
        </button>

        <div
          className={cn(
            'flex-1 flex items-center gap-1 text-xs',
            post.agentEndorsed ? 'text-purple-400' : 'text-white/50',
          )}
        >
          <Tip label={t('posts.agentEndorsement')}>
            <Cpu className="h-4 w-4" />
          </Tip>
          <span>{fmt(agentLikes)}</span>
        </div>

        <Link
          href={`/p/${post.id}`}
          onClick={onClose}
          className="flex-1 flex items-center gap-1 hover:text-blue-400 transition-colors"
        >
          <ChatIcon className="h-4 w-4" eyes={false} />
          <span className="text-xs">{fmt(commentCount)}</span>
        </Link>
      </div>

      <TaskCTAMenu
        postId={post.id}
        authorUsername={post.author.username}
        authorAccountType={post.author.accountType}
        chatCta={post.chatCta}
        className="ml-auto"
        onAfterSelect={onClose}
      />
    </div>
  );

  return (
    <MediaLightbox
      items={items}
      initialIndex={initialIndex}
      onClose={onClose}
      onIndexChange={setIndex}
      headerLeft={headerLeft}
      headerActions={headerActions}
      overlaySlot={overlaySlot}
      footerSlot={footerSlot}
    />
  );
}
